import { Trans, useTranslation } from 'next-i18next';
import type { RefObject } from 'react';
import { useEffect, useRef, useState } from 'react';

import { V1A_NAMESPACE, V1A_VIDEO } from '../constants';
import { useCountUp } from '../hooks/use-count-up';
import { useHtmlLang } from '../hooks/use-html-lang';
import { useReducedMotion } from '../hooks/use-reduced-motion';
import { formatInteger } from './media';
import { useLazyVideos } from './use-lazy-videos';

const LIST_CLASS = 'v1-panel__list mx-0 mb-0 mt-3.5 grid list-none gap-2 !px-0 !pb-0 !pt-3 *:min-w-0 [&_b]:text-end [&_b]:font-semibold [&_li]:flex [&_li]:flex-wrap [&_li]:items-baseline [&_li]:justify-between [&_li]:gap-3 [&_li]:text-[length:var(--t-micro)] [&_span]:tracking-[.04em] [&_span]:text-[#8C95A8]';
const VALUE_CLASS = 'tabular text-[length:clamp(2.75rem,1.6rem_+_3.4vw,4rem)] font-bold tracking-[-.045em]';
const BAD_GRAPH = 'M0 44 L16 22 L32 50 L48 14 L64 46 L80 26 L96 56 L112 18 L128 40 L144 10 L160 52 L176 30 L192 58 L208 20 L224 44 L240 12 L256 48 L272 28 L288 54 L304 22 L320 40';
const GOOD_GRAPH = 'M0 34 L16 32 L32 35 L48 33 L64 34 L80 32 L96 35 L112 33 L128 34 L144 32 L160 34 L176 33 L192 35 L208 32 L224 34 L240 33 L256 34 L272 32 L288 35 L304 33 L320 34';

type PanelKind = 'bad' | 'good';

function Panel({ kind, value, valueRef }: { kind: PanelKind; value: string; valueRef: RefObject<HTMLSpanElement> }) {
  const { t } = useTranslation(V1A_NAMESPACE);
  const isGood = kind === 'good';

  return (
    <article className={`v1-panel v1-panel--${kind} relative flex flex-col overflow-hidden px-4 pb-[1.125rem] pt-4 *:relative`}>
      <header className='v1-panel__head flex flex-wrap items-center justify-between gap-3'>
        <span className='v1-panel__tag px-2.5 py-[.3125rem] text-[length:var(--t-micro)] font-bold uppercase tracking-[.14em]'>{t(`shift.${kind}.tag`)}</span>
        <span className='v1-panel__state text-[length:var(--t-micro)] tracking-[.04em] text-[#8C95A8]'>{t(`shift.${kind}.state`)}</span>
      </header>
      <div className='v1-panel__media relative mt-3.5 aspect-[16/9] overflow-hidden'>
        <video className='v1-panel__video block size-full object-cover' muted loop playsInline preload='none' width='640' height='360' poster={`${V1A_VIDEO}/shift-${kind}-poster.webp`} data-lazy-video='' data-src-webm={`${V1A_VIDEO}/shift-${kind}.webm`} data-src-mp4={`${V1A_VIDEO}/shift-${kind}.mp4`} aria-label={t(`shift.${kind}.video_label`)} />
        <span className='v1-panel__scan' aria-hidden='true' />
        <span className='v1-panel__lower' aria-hidden='true'>
          <span className='v1-panel__lower-dot' />
          {t(`shift.${kind}.lower`)}
        </span>
      </div>
      <p className='v1-panel__value mx-0 mb-0 mt-3.5 flex items-baseline gap-[.3125rem] leading-[.9]'>
        <span ref={valueRef} className={`${isGood ? '!text-[#93FF18]' : 'text-[#FF5C6C]'} ${VALUE_CLASS}`}>
          {value}
        </span>
        <span className='v1-panel__unit text-[length:var(--t-lead)] font-semibold text-[#9AA3B5]'>ms</span>
      </p>
      <p className='v1-panel__vlabel mx-0 mb-0 mt-1 text-[length:var(--t-micro)] font-semibold uppercase tracking-[.12em] text-[#8C95A8]'>{t('shift.average_ping')}</p>
      <svg className='v1-panel__graph' viewBox='0 0 320 64' width='320' height='64' preserveAspectRatio='none' aria-hidden='true' focusable='false'>
        <path d={isGood ? GOOD_GRAPH : BAD_GRAPH} fill='none' stroke={isGood ? '#93FF18' : '#FF5C6C'} strokeWidth='2' strokeLinejoin='round' />
      </svg>
      {/* role=list: list-style none drops the list semantics in Safari/VoiceOver, as the design notes */}
      {/* eslint-disable-next-line jsx-a11y/no-redundant-roles */}
      <ul className={`${LIST_CLASS} ${isGood ? '[&_b]:text-[#D4FF27]' : '[&_b]:text-[#F4F6F7]'}`} role='list'>
        {(['loss', 'jitter', 'third'] as const).map((row) => (
          <li key={row}>
            <span>{t(`shift.${kind}.${row}.label`)}</span>
            <b>{t(`shift.${kind}.${row}.value`)}</b>
          </li>
        ))}
      </ul>
    </article>
  );
}

export function Shift() {
  const { t } = useTranslation(V1A_NAMESPACE);
  const htmlLang = useHtmlLang();
  const vsRef = useRef<HTMLDivElement>(null);
  const reduced = useReducedMotion();
  // One pill per moving block (WCAG 2.2.2). Under reduced motion the clips start held, and a live flip of
  // the OS setting is honoured too; the visitor can still opt in.
  const [paused, setPaused] = useState(false);
  useEffect(() => setPaused(reduced), [reduced]);
  useLazyVideos(vsRef, paused);

  const [badRef, bad] = useCountUp<HTMLSpanElement>(208, { format: (value) => formatInteger(htmlLang, value) });
  const [goodRef, good] = useCountUp<HTMLSpanElement>(42, { format: (value) => formatInteger(htmlLang, value) });

  return (
    <section className='v1-shift relative isolate overflow-x-clip border-t border-[rgb(255_255_255/.08)] bg-[linear-gradient(180deg,#0B0D10,#0A0C10)] py-[clamp(3rem,1.5rem_+_5vw,6rem)]' aria-labelledby='v1-shift-title'>
      <div className='v1-shift__bg' aria-hidden='true' />
      <div className='mx-auto w-full max-w-[90rem] px-[var(--gutter)]'>
        <div className='v1-head v1-head--center mx-auto max-w-[46rem] text-center [&_.v1-lead]:mx-auto'>
          <p className='v1-eyebrow v1-eyebrow--center m-0 inline-flex items-center justify-center gap-2 text-[length:var(--t-micro)] font-semibold uppercase leading-[1.3] tracking-[.16em] text-[#9AA3B5]' data-reveal=''>
            <span className='v1-eyebrow__dot size-1.5 flex-none rounded-full bg-[#93FF18] shadow-[0_0_10px_rgb(147_255_24/.8)]' aria-hidden='true' />
            {t('shift.eyebrow')}
          </p>
          <h2 className='v1-h2 mt-3 text-balance text-[length:var(--t-h2)] font-bold leading-[1.04] tracking-[-.032em] text-[#FFFFFF] min-[1920px]:text-[length:3.5rem]' id='v1-shift-title' data-reveal=''>
            <Trans t={t} i18nKey='shift.title' components={{ hl: <span className='v1-hl !text-[#93FF18] [text-shadow:0_0_22px_rgb(147_255_24/.3)] forced-colors:!text-[LinkText]' /> }} />
          </h2>
          <p className='v1-lead mt-4 max-w-[44rem] text-pretty text-[length:var(--t-lead)] leading-[1.55] text-[#CBD5E1]' data-reveal=''>
            {t('shift.lead')}
          </p>
        </div>

        <div className='v1-mo-bar v1-mo-bar--top -mb-2.5 mt-[clamp(1.75rem,1rem_+_2.4vw,2.5rem)] flex justify-end max-lg:mx-auto max-lg:max-w-2xl' data-reveal=''>
          <button className='v1-mo' type='button' data-motion='videos' aria-pressed={paused} aria-label={t(paused ? 'shift.motion.play_aria' : 'shift.motion.pause_aria')} title={t('shift.motion.title')} onClick={() => setPaused((previous) => !previous)}>
            <svg className='v1-mo__ic v1-mo__ic--pause' viewBox='0 0 24 24' width='14' height='14' aria-hidden='true' focusable='false'>
              <path d='M8.5 5.5v13M15.5 5.5v13' />
            </svg>
            <svg className='v1-mo__ic v1-mo__ic--play' viewBox='0 0 24 24' width='14' height='14' aria-hidden='true' focusable='false'>
              <path d='M8 5.2v13.6a.8.8 0 0 0 1.2.7l11-6.8a.8.8 0 0 0 0-1.4l-11-6.8a.8.8 0 0 0-1.2.7z' />
            </svg>
            <span data-motion-label=''>{t(paused ? 'shift.motion.play' : 'shift.motion.pause')}</span>
          </button>
        </div>

        <div ref={vsRef} className='v1-vs mt-[clamp(2rem,1rem_+_3vw,3rem)] grid items-stretch gap-4 *:min-w-0 max-lg:mx-auto max-lg:max-w-2xl lg:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] lg:gap-[clamp(1rem,.25rem_+_1.6vw,2rem)]' data-reveal=''>
          <Panel kind='bad' value={bad} valueRef={badRef} />

          <div className='v1-vs__mid flex flex-row items-center justify-center gap-3 py-1 lg:flex-col lg:gap-3.5 lg:py-0' aria-hidden='true'>
            <span className='v1-vs__line h-px flex-1 lg:h-auto lg:w-px' />
            <span className='v1-vs__badge grid size-12 flex-none place-items-center text-[length:var(--t-micro)] font-bold tracking-[.1em] text-[#FFFFFF]'>VS</span>
            <span className='v1-vs__delta inline-flex flex-none items-center gap-1.5 whitespace-nowrap px-[.5625rem] py-[.3125rem] text-[length:var(--fs-md)] font-bold leading-[1.1] tracking-[-.01em] !text-[#93FF18] lg:flex-col lg:gap-[.1875rem] lg:px-[.5625rem] lg:pb-2 lg:pt-[.4375rem] [&_small]:ps-1.5 [&_small]:text-[length:var(--t-micro)] [&_small]:font-semibold [&_small]:tracking-[.04em] [&_small]:text-[#8C95A8] lg:[&_small]:ps-0'>
              −80%<small>−166 ms</small>
            </span>
            <span className='v1-vs__line h-px flex-1 lg:h-auto lg:w-px' />
          </div>

          <Panel kind='good' value={good} valueRef={goodRef} />
        </div>

        <p className='v1-shift__note mx-auto mt-[clamp(1.5rem,1rem_+_1.5vw,2rem)] max-w-[46rem] text-center text-[length:max(.75rem,var(--t-micro))] leading-[1.6] text-[#8C95A8] max-lg:mx-auto max-lg:max-w-2xl [&_strong]:font-semibold [&_strong]:text-[#CBD5E1]' data-reveal=''>
          <Trans t={t} i18nKey='shift.note' components={{ strong: <strong /> }} />
        </p>
      </div>
    </section>
  );
}
